import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  Badge,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Radio as RadioIcon,
  VideoLibrary as VideoLibraryIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { apiService, Channel, Show, Episode, Panelist } from '../services/api';
import Breadcrumb from './Breadcrumb';

const ShowDetail: React.FC = () => {
  const navigate = useNavigate();
  const { showId } = useParams<{ showId: string }>();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [show, setShow] = useState<Show | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [panelists, setPanelists] = useState<Panelist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<Show | null>(null);
  const [openPanelistDialog, setOpenPanelistDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    status: 'draft' as 'active' | 'inactive' | 'draft' | 'archived',
    frequency: undefined as 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom' | undefined,
  });
  
  // Episode creation dialog state
  const [openEpisodeDialog, setOpenEpisodeDialog] = useState(false);
  const [episodeFormData, setEpisodeFormData] = useState({
    title: '',
    description: '',
    status: 'draft' as 'draft' | 'published' | 'archived',
    scheduledFor: '',
    tags: [] as string[],
  });

  useEffect(() => {
    if (showId) {
      fetchData();
    }
  }, [showId]);

  const fetchData = async () => {
    if (!showId) return;
    
    try {
      setLoading(true);
      const [showData, episodesData, panelistsData] = await Promise.all([
        apiService.getShow(showId),
        apiService.getEpisodes(),
        apiService.getPanelists(),
      ]);
      setShow(showData);
      setEpisodes(episodesData.filter(episode => episode.showId === showId));
      setPanelists(panelistsData);
      
      // Fetch channel data if we have a show
      if (showData.channelId) {
        try {
          const channelData = await apiService.getChannel(showData.channelId);
          setChannel(channelData);
        } catch (error) {
          console.error('Error fetching channel data:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item: Show) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      category: item.category || '',
      status: item.status,
      frequency: item.frequency,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      status: 'draft',
      frequency: undefined,
    });
  };

  const handleSubmit = async () => {
    if (!editingItem) return;
    
    try {
      await apiService.updateShow(editingItem._id, formData);
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error updating show:', error);
      setError('Failed to update show');
    }
  };

  const handleDelete = async () => {
    if (!show) return;
    
    if (window.confirm('Are you sure you want to delete this show? This will also delete all associated episodes.')) {
      try {
        await apiService.deleteShow(show._id);
        navigate(`/channels/${channel?._id}`);
      } catch (error) {
        console.error('Error deleting show:', error);
        setError('Failed to delete show');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      case 'published': return 'info';
      default: return 'default';
    }
  };

  const handleAddPanelistToShow = async (panelistId: string) => {
    if (!show || show.panelistIds.includes(panelistId)) {
      setError('Panelist is already assigned to this show');
      return;
    }

    try {
      const updatedPanelistIds = [...show.panelistIds, panelistId];
      await apiService.updateShow(show._id, { panelistIds: updatedPanelistIds });
      setShow(prev => prev ? { ...prev, panelistIds: updatedPanelistIds } : null);
      setOpenPanelistDialog(false);
    } catch (error) {
      console.error('Error adding panelist to show:', error);
      setError('Failed to add panelist to show');
    }
  };

  const handleRemovePanelistFromShow = async (panelistId: string) => {
    if (!show || !show.panelistIds.includes(panelistId)) {
      setError('Panelist is not assigned to this show');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this panelist from the show?')) {
      return;
    }

    try {
      const updatedPanelistIds = show.panelistIds.filter(id => id !== panelistId);
      await apiService.updateShow(show._id, { panelistIds: updatedPanelistIds });
      setShow(prev => prev ? { ...prev, panelistIds: updatedPanelistIds } : null);
    } catch (error) {
      console.error('Error removing panelist from show:', error);
      setError('Failed to remove panelist from show');
    }
  };

  // Episode creation handlers
  const handleOpenEpisodeDialog = () => {
    setEpisodeFormData({
      title: '',
      description: '',
      status: 'draft',
      scheduledFor: '',
      tags: [],
    });
    setOpenEpisodeDialog(true);
  };

  const handleCloseEpisodeDialog = () => {
    setOpenEpisodeDialog(false);
    setEpisodeFormData({
      title: '',
      description: '',
      status: 'draft',
      scheduledFor: '',
      tags: [],
    });
  };

  const handleCreateEpisode = async () => {
    if (!showId || !show?.channelId) return;
    
    try {
      const episodeData = {
        ...episodeFormData,
        showId: showId,
        channelId: show.channelId,
      };
      
      await apiService.createEpisode(episodeData);
      handleCloseEpisodeDialog();
      fetchData(); // Refresh the episodes list
    } catch (error) {
      console.error('Error creating episode:', error);
      setError('Failed to create episode');
    }
  };

  const getAssignedPanelists = () => {
    return panelists.filter(panelist => show?.panelistIds.includes(panelist._id));
  };

  const getAvailablePanelists = () => {
    return panelists.filter(panelist => !show?.panelistIds.includes(panelist._id));
  };

  if (loading) {
    return (
      <Box>
        <Breadcrumb items={[
          { label: 'Channels', path: '/channels', icon: <RadioIcon /> },
          { label: channel?.name || 'Loading...', path: `/channels/${channel?._id}`, icon: <RadioIcon /> },
          { label: show?.name || 'Loading...', path: `/shows/${showId}`, icon: <RadioIcon /> }
        ]} />
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Breadcrumb items={[
          { label: 'Channels', path: '/channels', icon: <RadioIcon /> },
          { label: channel?.name || 'Channel', path: `/channels/${channel?._id}`, icon: <RadioIcon /> },
          { label: show?.name || 'Show', path: `/shows/${showId}`, icon: <RadioIcon /> }
        ]} />
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      </Box>
    );
  }

  if (!channel || !show) {
    return (
      <Box>
        <Breadcrumb items={[
          { label: 'Channels', path: '/channels', icon: <RadioIcon /> },
          { label: 'Channel Not Found', path: `/channels/${channel?._id}`, icon: <RadioIcon /> },
          { label: 'Show Not Found', path: `/shows/${showId}`, icon: <RadioIcon /> }
        ]} />
        <Alert severity="error" sx={{ m: 2 }}>Channel or show not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumb items={[
        { label: 'Channels', path: '/channels', icon: <RadioIcon /> },
        { label: channel.name, path: `/channels/${channel._id}`, icon: <RadioIcon /> },
        { label: show.name, path: `/shows/${showId}`, icon: <RadioIcon /> }
      ]} />
      
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1">
              {show.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Channel: {channel.name}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => handleOpenDialog(show)}
              sx={{ mr: 2 }}
            >
              Edit Show
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                // Scroll to episodes section since episodes are now displayed on the same page
                const episodesSection = document.getElementById('episodes-section');
                if (episodesSection) {
                  episodesSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              startIcon={<VideoLibraryIcon />}
            >
              View Episodes ({episodes.length})
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Main Content Area */}
          <Grid item xs={12} lg={8}>
            {/* Show Information */}
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Show Information
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Chip 
                    label={show.status} 
                    color={getStatusColor(show.status) as any}
                    size="small"
                  />
                  {show.frequency && (
                    <Chip label={show.frequency} variant="outlined" size="small" />
                  )}
                  {show.category && (
                    <Chip label={show.category} variant="outlined" size="small" />
                  )}
                </Box>

                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {show.description || 'No description available'}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    Show ID:
                  </Typography>
                  <Typography variant="caption" fontFamily="monospace">
                    {show._id}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Episodes Section */}
            <Card id="episodes-section">
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Episodes ({episodes.length})
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={handleOpenEpisodeDialog}
                    sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      borderRadius: 1.5,
                      px: 2,
                      py: 0.5,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                      },
                    }}
                  >
                    Add Episode
                  </Button>
                </Box>

                {episodes.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <VideoLibraryIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1.5 }} />
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      No episodes in this show yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Create your first episode to get started
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={handleOpenEpisodeDialog}
                      sx={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        borderRadius: 1.5,
                        px: 2,
                        py: 0.5,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                        },
                      }}
                    >
                      Add First Episode
                    </Button>
                  </Box>
                ) : (
                  <Grid container spacing={1.5}>
                    {episodes.map((episode) => (
                      <Grid item xs={12} sm={6} md={4} key={episode._id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              transform: 'translateY(-1px)',
                            }
                          }}
                          onClick={() => navigate(`/episodes/${episode._id}`)}
                        >
                          <CardContent sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                              {episode.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                              {episode.description || 'No description available'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                              <Chip 
                                label={episode.status} 
                                size="small" 
                                color={getStatusColor(episode.status) as any}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                              {episode.episodeNumber && (
                                <Chip 
                                  label={`Ep ${episode.episodeNumber}`}
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                              {episode.duration && (
                                <Chip 
                                  label={episode.duration}
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                            </Box>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/episodes/${episode._id}`);
                              }}
                              fullWidth
                              sx={{ fontSize: '0.7rem', py: 0.5 }}
                            >
                              View Details
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            {/* Quick Stats */}
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Quick Stats
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <VideoLibraryIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 20 }} />
                  <Box>
                    <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
                      {episodes.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Episodes
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<VideoLibraryIcon />}
                  size="small"
                  onClick={() => {
                    const episodesSection = document.getElementById('episodes-section');
                    if (episodesSection) {
                      episodesSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  disabled={episodes.length === 0}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    borderRadius: 1.5,
                    py: 0.5,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    },
                  }}
                >
                  {episodes.length === 0 ? 'No Episodes' : 'Manage Episodes'}
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<VideoLibraryIcon />}
                  size="small"
                  onClick={handleOpenEpisodeDialog}
                  sx={{ mt: 1, borderRadius: 1.5, py: 0.5 }}
                >
                  Add New Episode
                </Button>
              </CardContent>
            </Card>

            {/* Panelists */}
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Panelists ({show.panelistIds.length})
                </Typography>
                
                {getAssignedPanelists().length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                      No panelists assigned to this show
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenPanelistDialog(true)}
                      sx={{ borderRadius: 1.5, py: 0.5 }}
                    >
                      Add Panelist
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    {getAssignedPanelists().map((panelist) => (
                      <Box key={panelist._id} sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1
                      }}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
                            {panelist.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {panelist.character} • {panelist.voiceName}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemovePanelistFromShow(panelist._id)}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setOpenPanelistDialog(true)}
                      sx={{ mt: 1, borderRadius: 1.5, py: 0.5 }}
                    >
                      Add More Panelists
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Actions
                </Typography>
                
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenDialog(show)}
                  size="small"
                  sx={{ mb: 1, borderRadius: 1.5, py: 0.5 }}
                >
                  Edit Show
                </Button>
                
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  size="small"
                  sx={{ borderRadius: 1.5, py: 0.5 }}
                >
                  Delete Show
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Edit Show Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Show
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Show Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={formData.frequency || ''}
                label="Frequency"
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Panelist Dialog */}
      <Dialog open={openPanelistDialog} onClose={() => setOpenPanelistDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Add Panelist to Show
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {getAvailablePanelists().length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  All panelists are already assigned to this show
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create new panelists in the Panelists section to add them to this show
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {getAvailablePanelists().map((panelist) => (
                  <Grid item xs={12} sm={6} md={4} key={panelist._id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 2,
                          borderColor: 'primary.main',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                      onClick={() => handleAddPanelistToShow(panelist._id)}
                    >
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {panelist.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {panelist.character}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                          Voice: {panelist.voiceName}
                        </Typography>
                        {panelist.expertise && panelist.expertise.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {panelist.expertise.slice(0, 2).map((skill, index) => (
                              <Chip key={index} label={skill} size="small" variant="outlined" />
                            ))}
                            {panelist.expertise.length > 2 && (
                              <Chip label={`+${panelist.expertise.length - 2}`} size="small" variant="outlined" />
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPanelistDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Episode Dialog */}
      <Dialog open={openEpisodeDialog} onClose={handleCloseEpisodeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Create New Episode
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Episode Title"
                  value={episodeFormData.title}
                  onChange={(e) => setEpisodeFormData({ ...episodeFormData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={episodeFormData.description}
                  onChange={(e) => setEpisodeFormData({ ...episodeFormData, description: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={episodeFormData.status}
                    onChange={(e) => setEpisodeFormData({ ...episodeFormData, status: e.target.value as any })}
                    label="Status"
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Scheduled For"
                  type="datetime-local"
                  value={episodeFormData.scheduledFor}
                  onChange={(e) => setEpisodeFormData({ ...episodeFormData, scheduledFor: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEpisodeDialog}>Cancel</Button>
          <Button onClick={handleCreateEpisode} variant="contained" disabled={!episodeFormData.title.trim()}>
            Create Episode
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShowDetail;
